import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Divider,
  Field,
  Input,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkRegular, ClockRegular, MailRegular, PersonAvailableRegular } from '@fluentui/react-icons';
import { Shell, ContactFooter } from '../components/Shell';
import { IconCircle, IconList, Lead, Steps, Title } from '../components/ui';
import { postJson, ApiError } from '../lib/api';
import { checkWorkEmail } from '../lib/emailRules';
import { consentStartUrl, useSignup, validSignupId } from '../lib/signupState';

const useStyles = makeStyles({
  copyRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' },
  form: { display: 'grid', gap: '12px' },
  primary: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
});

export function Done() {
  const [params] = useSearchParams();
  const { state } = useSignup();
  const status = params.get('status');
  const signupId = validSignupId(params.get('id')) || validSignupId(state.signupId);

  if (status === 'ok' || status === 'already') return <Success already={status === 'already'} />;
  if (!signupId) return <Missing />;
  return <AdminHandoff signupId={signupId} />;
}

function Success({ already }: { already: boolean }) {
  return (
    <Shell
      labelledBy="title"
      brandTitle="What happens next"
      brandTagline="TELLEC Docs scans your tenant overnight. Your first findings arrive by email tomorrow morning."
      footerLeft={<ContactFooter />}
    >
      <Steps step={3} of={3} label="All done" />
      <IconCircle tone="ok">
        <CheckmarkRegular />
      </IconCircle>
      {already ? (
        <>
          <Title id="title">You're already set up.</Title>
          <Lead>
            Your tenant already has a TELLEC NFR licence, so there was nothing new to do. Findings keep arriving as
            normal.
          </Lead>
        </>
      ) : (
        <>
          <Title id="title">Done — first findings tomorrow morning.</Title>
          <Lead>Your NFR licence is active and your first scan is queued for tonight.</Lead>
        </>
      )}
      <IconList
        label="What happens next"
        items={[
          {
            icon: <MailRegular />,
            title: 'Check your inbox',
            text: 'Confirmation with a PDF of your NFR terms, from docs@mail.tellec.com.',
          },
          {
            icon: <ClockRegular />,
            title: 'Tomorrow morning',
            text: 'Guest access, external sharing and storage findings for your tenant.',
          },
        ]}
      />
    </Shell>
  );
}

function AdminHandoff({ signupId }: { signupId: string }) {
  const s = useStyles();
  const link = consentStartUrl(signupId);
  const [copyStatus, setCopyStatus] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);

  async function copy() {
    try {
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        await navigator.share({ title: 'TELLEC NFR admin consent', url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      setCopyStatus('Link copied.');
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return; // share sheet dismissed
      setCopyStatus('Select the link and copy it.');
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    setSentTo('');
    const value = email.trim();
    const msg = value ? await checkWorkEmail(value) : 'Enter their email, or copy the link above instead.';
    setEmailError(msg);
    if (msg) return;
    setBusy(true);
    try {
      await postJson('/api/consent/send-to-admin', { signupId, email: value });
      setSentTo(value);
      setEmail('');
    } catch (err) {
      setEmailError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell
      labelledBy="title"
      brandTitle="Nearly there"
      brandTagline="Your terms are saved. A Global Admin approves access for TELLEC Docs and findings follow the next morning."
      footerLeft={<ContactFooter />}
    >
      <Steps step={3} of={3} />
      <IconCircle tone="warn">
        <PersonAvailableRegular />
      </IconCircle>
      <Title id="title">Not a Global Admin? Send this link to the person who is.</Title>
      <Lead>
        Your terms are accepted. A Microsoft 365 Global Admin for your tenant just needs to open this link and approve
        access for TELLEC Docs. Findings follow the next morning.
      </Lead>

      <Field label="Admin consent link" hint={copyStatus} validationState={copyStatus ? 'success' : undefined}>
        <div className={s.copyRow}>
          <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Admin consent link" />
          <Button onClick={copy}>Copy link</Button>
        </div>
      </Field>

      <Divider />

      <form className={s.form} noValidate onSubmit={send}>
        <Field
          label="Or we'll email it to them (optional)"
          validationState={emailError ? 'error' : undefined}
          validationMessage={emailError || undefined}
        >
          <Input
            size="large"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={254}
            placeholder="admin@yourmsp.com.au"
            value={email}
            onChange={(_, v) => setEmail(v.value)}
          />
        </Field>
        <Button
          appearance="primary"
          size="large"
          type="submit"
          className={s.primary}
          disabled={busy}
          icon={busy ? <Spinner size="tiny" appearance="inverted" /> : undefined}
        >
          {busy ? 'Sending…' : 'Email the link'}
        </Button>
        {sentTo && (
          <MessageBar intent="success">
            <MessageBarBody>Sent to {sentTo}. We'll email you once they've approved it.</MessageBarBody>
          </MessageBar>
        )}
      </form>
    </Shell>
  );
}

function Missing() {
  const s = useStyles();
  const navigate = useNavigate();
  return (
    <Shell labelledBy="title" brandTitle="Let's start again" brandTagline="It only takes a minute." footerLeft={<ContactFooter />}>
      <Title id="title">We couldn't find your sign-up</Title>
      <Lead>This link is missing its sign-up reference. Start again and it only takes a minute.</Lead>
      <Button appearance="primary" size="large" className={s.primary} onClick={() => navigate('/details')}>
        Start again
      </Button>
    </Shell>
  );
}
